export const STUDIO_SCENARIO_PREVIEW_CHANNELS = [
  "internal_preview",
  "demo_driver",
  "sandbox_rehearsal",
  "voice_quality_lab",
] as const;

export type StudioScenarioPreviewChannel =
  (typeof STUDIO_SCENARIO_PREVIEW_CHANNELS)[number];

export type StudioScenarioPresetId =
  | "receptionist_greeting"
  | "scheduling_assist"
  | "human_handoff"
  | "objection_handling"
  | "payment_not_ready"
  | "distance_timing_hesitation"
  | "renewal_clarification"
  | "transfer_intake_clarification"
  | "veteran_funding_boundary"
  | "distance_location_renewal_transfer"
  | "callback_rescue"
  | "transfer_payment_fallback"
  | "premium_voice_polish_preview";

export type StudioScenarioPreset = {
  id: StudioScenarioPresetId;
  label: string;
  summary: string;
  operatorGoal: string;
  channel: StudioScenarioPreviewChannel;
  transcriptLines: string[];
};

export type StudioScenarioPayload = {
  scenarioId: StudioScenarioPresetId;
  scenarioLabel: string;
  channel: StudioScenarioPreviewChannel;
  correlationId: string;
  transcriptLines: string[];
  text: string;
};

export const STUDIO_SCENARIO_PRESETS: StudioScenarioPreset[] = [
  {
    id: "receptionist_greeting",
    label: "Receptionist greeting",
    summary:
      "Demonstrate a polished first hello that sets scope, tone, and next-step clarity.",
    operatorGoal:
      "Show a calm, premium welcome with a cleaner first impression and no implied live clinic activation beyond current shipped behavior.",
    channel: "internal_preview",
    transcriptLines: [
      "Thank you for calling VetCan.",
      "I can help with hours, current supported next steps, supported renewals, or a clean staff handoff.",
      "What would you like to handle first?",
    ],
  },
  {
    id: "scheduling_assist",
    label: "Scheduling assist",
    summary:
      "Preview concise front-desk scheduling that keeps the next step clear while keeping edge cases with staff.",
    operatorGoal:
      "Show how the assistant keeps scheduling context clear, stays inside current supported preview wording, and hands unusual cases to staff.",
    channel: "demo_driver",
    transcriptLines: [
      "I can keep this simple.",
      "Tell me the day you want, and I can keep the current supported next step clear before we lock in booking details.",
      "If anything is unusual, I can bring in staff right away so the handoff stays clean.",
    ],
  },
  {
    id: "human_handoff",
    label: "Human handoff",
    summary:
      "Demonstrate an immediate, professional staff handoff when the request needs a person.",
    operatorGoal:
      "Rehearse a calm transition to staff without drifting into medical advice or PHI collection.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "I want to make sure this gets handled cleanly.",
      "I am going to bring in a staff member now so you have a direct handoff.",
      "Thank you for staying with me while I connect the next step.",
    ],
  },
  {
    id: "objection_handling",
    label: "Objection handling",
    summary:
      "Preview a warm, business-class response to hesitation or skepticism.",
    operatorGoal:
      "Show calm reassurance and a clear next step without hype or pressure.",
    channel: "demo_driver",
    transcriptLines: [
      "That is a fair question.",
      "I can keep this brief, explain the next step clearly, and involve staff immediately if you want a more specific answer.",
      "Happy to help you choose the simplest path forward.",
    ],
  },
  {
    id: "payment_not_ready",
    label: "Payment not ready",
    summary:
      "Rehearse a calm response when the caller is interested but not ready to handle payment today.",
    operatorGoal:
      "Keep payment wording operational, offer a staff-safe follow-up path, and avoid pressure or payment-authority drift.",
    channel: "demo_driver",
    transcriptLines: [
      "That is completely fine.",
      "If today is not the right time to handle payment, I can keep the next step simple and have staff follow up when the timing works better for you.",
      "We can pause here without losing the clean handoff.",
    ],
  },
  {
    id: "distance_timing_hesitation",
    label: "Distance/timing hesitation",
    summary:
      "Rehearse a practical reply when distance or timing makes the visit feel harder to plan.",
    operatorGoal:
      "Acknowledge operational friction, keep the path realistic, and move to staff-safe follow-up without implying unsupported flexibility.",
    channel: "demo_driver",
    transcriptLines: [
      "I understand that hesitation.",
      "If distance or timing makes this harder to plan, I can keep the next step practical and have staff help with the cleanest follow-up path.",
      "That keeps the handoff accurate without guessing on exceptions.",
    ],
  },
  {
    id: "renewal_clarification",
    label: "Renewal clarification",
    summary:
      "Rehearse a bounded reply when the caller asks about the state-card timeline versus the office seven-month renewal path, or telephone renewal handling.",
    operatorGoal:
      "Keep the state-card timeline distinct from the office seven-month renewal path, and keep telephone handling staff-confirmed without implying broader renewal authority.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "That is a good clarification to make.",
      "If this is about the state-card timeline versus the office seven-month renewal path, or whether telephone handling applies, staff can confirm the current renewal path without guesswork.",
      "That keeps the renewal next step accurate and rehearsal-safe.",
    ],
  },
  {
    id: "transfer_intake_clarification",
    label: "Transfer intake clarification",
    summary:
      "Rehearse a staff-safe reply when transfer intake depends on first-visit path or telehealth clarification.",
    operatorGoal:
      "Keep first-visit path, telehealth status, and next-step ownership explicit without implying a public telehealth promise or completed transfer.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "I can keep that clarification clean.",
      "If this transfer question depends on whether it stays on a first-visit path, whether telehealth is part of the current public path, and who owns the next operational step, staff can confirm that directly.",
      "That keeps ownership accurate without overstating the transfer path.",
    ],
  },
  {
    id: "veteran_funding_boundary",
    label: "Veteran/funding boundary",
    summary:
      "Rehearse a bounded handoff when veteran pricing or funding-style questions need staff confirmation.",
    operatorGoal:
      "Keep veteran pricing and funding language inside the approved lane without guessing on exceptions or non-public support paths.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "The safest next step is staff confirmation.",
      "If this question is about veteran pricing or any funding-style help, staff can confirm the current approved path without guessing on exceptions.",
      "That keeps the wording accurate, bounded, and review-safe.",
    ],
  },
  {
    id: "distance_location_renewal_transfer",
    label: "Distance/location renewal-transfer hesitation",
    summary:
      "Rehearse a practical reply when distance or location makes a renewal or transfer visit harder to plan.",
    operatorGoal:
      "Keep the next step practical and location-sensitive without implying unsupported exceptions for renewal or transfer routing.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "I understand that planning concern.",
      "If distance or location is making a renewal or transfer visit hard to plan, staff can confirm the most practical next step or callback timing.",
      "That keeps the handoff accurate without implying location exceptions that are not confirmed.",
    ],
  },
  {
    id: "callback_rescue",
    label: "Callback rescue",
    summary:
      "Rehearse a missed-call recovery reply that reassures the caller and makes the recovery path explicit.",
    operatorGoal:
      "Keep the response calm, explain the callback recovery path in bounded language, and avoid blame or unsupported callback certainty.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "No problem.",
      "If you missed the call, staff can help restart the callback path and confirm the best number or time window for the next step.",
      "I can keep that recovery handoff clear from here.",
    ],
  },
  {
    id: "transfer_payment_fallback",
    label: "Transfer/payment fallback",
    summary:
      "Rehearse a bounded handoff when the next step depends on scheduler or payment-owner confirmation.",
    operatorGoal:
      "Keep ownership explicit, avoid implying a clean live transfer, and move to the safest staff follow-up path.",
    channel: "sandbox_rehearsal",
    transcriptLines: [
      "The safest next step is a staff follow-up.",
      "If scheduler or payment-owner confirmation is still needed, I can keep the handoff clear without implying the transfer is already complete.",
      "That keeps payment timing and next-step ownership accurate.",
    ],
  },
  {
    id: "premium_voice_polish_preview",
    label: "Premium voice polish preview",
    summary:
      "Use the voice-quality lab lane to compare premium pacing, warmth, and close quality.",
    operatorGoal:
      "Inspect a polished talk track in the isolated quality lane before any canary discussion.",
    channel: "voice_quality_lab",
    transcriptLines: [
      "Of course, I can help with that.",
      "The next step is straightforward, and I can keep this clear from start to finish.",
      "Thank you for taking a moment with VetCan today.",
    ],
  },
];

export function getStudioScenarioPreset(
  presetId: StudioScenarioPresetId
): StudioScenarioPreset {
  return (
    STUDIO_SCENARIO_PRESETS.find((preset) => preset.id === presetId) ||
    STUDIO_SCENARIO_PRESETS[0]
  );
}

export function isStudioScenarioPresetId(
  value: string | null | undefined
): value is StudioScenarioPresetId {
  return STUDIO_SCENARIO_PRESETS.some((preset) => preset.id === value);
}

export function buildStudioScenarioPayload(
  preset: StudioScenarioPreset
): StudioScenarioPayload {
  return {
    scenarioId: preset.id,
    scenarioLabel: preset.label,
    channel: preset.channel,
    correlationId: `studio_demo_${preset.id}`,
    transcriptLines: [...preset.transcriptLines],
    text: preset.transcriptLines.join(" "),
  };
}
